'use client';

import 'mapbox-gl/dist/mapbox-gl.css';

import { Feature, FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import type { GeoJSONSource, LngLatLike, Map } from 'mapbox-gl';
import { useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useRouter } from 'next/navigation';

import { useSchemes } from '@personly/libs-providers';

import type { Configs } from '@/statics/configs';
import type { PersonalPlace } from '@/statics/personals';

interface Props {
  configs: Configs;
  personalPlaces: Array<PersonalPlace>;
  zoom: number;
}

interface MapOptions {
  container: HTMLElement;
  style: string;
  zoom: number;
  center?: LngLatLike;
}

const PersonalPlacesMap = ({ configs, personalPlaces, zoom }: Props) => {
  const { cssVariables } = useSchemes();
  const router = useRouter();

  const map = useRef<Map | null>(null);
  const mapContainer = useRef(null);

  const features = useMemo<Array<Feature<Point, GeoJsonProperties>>>(
    () =>
      personalPlaces
        .flatMap((personalPlace) =>
          personalPlace.children.map((child) => ({
            ...child,
            slug: personalPlace.slug,
          }))
        )
        .filter(
          ({ personalCount, matchCount }) => personalCount > 0 || matchCount > 0
        )
        .map(({ personalCount, location, slug }) => ({
          type: 'Feature',
          properties: { count: personalCount, slug },
          geometry: {
            type: 'Point',
            coordinates: [location.longitude, location.latitude],
          },
        })),
    [personalPlaces]
  );

  const data = useMemo<FeatureCollection<Point>>(
    () => ({ type: 'FeatureCollection', features }),
    [features]
  );

  useEffect(() => {
    if (map.current) return;

    mapboxgl.accessToken = configs.MAPBOX_PUBLIC_KEY;

    const mapOptions: MapOptions = {
      container: mapContainer.current!,
      style: 'mapbox://styles/mapbox/streets-v11?optimize=true',
      zoom,
    };

    if (data.features.length > 0) {
      mapOptions.center = data.features[0].geometry.coordinates as LngLatLike;
    }

    map.current = new mapboxgl.Map(mapOptions);
    map.current.addControl(new mapboxgl.NavigationControl());

    map.current.on('load', () => {
      map.current!.addSource('personals', { type: 'geojson', data });

      map.current!.addLayer({
        id: 'personals-circles',
        type: 'circle',
        source: 'personals',
        paint: {
          'circle-color': cssVariables.headerBackgroundColor[0],
          'circle-opacity': 0.3,
          'circle-radius': 20,
        },
      });

      map.current!.addLayer({
        id: 'personals-count',
        type: 'symbol',
        source: 'personals',
        layout: {
          'text-field': ['get', 'count'],
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: { 'text-color': 'rgba(0,0,0,0.5)' },
      });

      map.current!.on('click', 'personals-circles', (e) => {
        // @ts-expect-error properties
        const { slug } = e.features![0].properties;
        router.push(`/personals/${slug}`);
      });
    });
  }, [data, features, configs, zoom, cssVariables, router]);

  useEffect(() => {
    if (!map.current) return;
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition((position) => {
      map.current!.setCenter([
        position.coords.longitude,
        position.coords.latitude,
      ]);
    });
  }, []);

  useEffect(() => {
    if (!map.current) return;
    if (!map.current.loaded()) return;

    map.current.getSource<GeoJSONSource>('personals')?.setData(data);
  }, [data]);

  return <div ref={mapContainer} className="h-full" />;
};

export default PersonalPlacesMap;
